import { ALL_OBSERVATION_OPTIONS_MAP } from '@constants/option-types/vital-signs';

import type { ReducedObservationComponent } from '@models/observation';

/**
 * Seed record for the Vital Sign / Body curation form.
 *
 * `unit` is not a measurement unit for the form to display — every component in
 * every set has exactly one unit, so `getMeasurementArray` fills those in
 * itself. It exists only to tell FormDrilldown's effect which component *set*
 * this reading belongs to, by matching against the first unit of each set.
 */
export type VitalSignOcrRecord = {
  code: string;
  testDate: string;
  unit: string | null;
  components: ReducedObservationComponent[];
};

type ComponentOption = { code?: string; display?: string; units: string[] };

const BLOOD_PRESSURE = 'blood-pressure';
const BODY_HEIGHT = 'body-height';

/** "116/73", "116 / 73", "116/73 mmHg". */
const BLOOD_PRESSURE_PATTERN =
  /^\s*(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)\s*(?:mmhg)?\s*$/i;

/** 6'0" · 6' 0" · 5'11 · 6' · 6ft 0in · 5 ft 11 in */
const FEET_INCHES_PATTERN =
  /^\s*(\d+(?:\.\d+)?)\s*(?:'|ft|feet)\s*(?:(\d+(?:\.\d+)?)\s*(?:"|''|in|inch|inches)?)?\s*$/i;

/** "183 cm" — a lossless /100 onto the metric component set. */
const CENTIMETRES_PATTERN = /^\s*(\d+(?:\.\d+)?)\s*cm\s*$/i;

/** "1.83 m" or a bare "1.83". */
const METRES_PATTERN = /^\s*(\d+(?:\.\d+)?)\s*m?\s*$/i;

function asString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * Component sets for a code, straight from the option map. An unknown code has
 * none: discern's `Vital_signs` Literal permits `waist-circumference`, which is
 * in neither of Engage's option maps, and the form guards it via
 * `isSupportedObservationCode`. Manufacturing rows here would defeat that.
 */
function componentSets(code: string): ComponentOption[][] {
  if (!(code in ALL_OBSERVATION_OPTIONS_MAP)) return [];
  return ALL_OBSERVATION_OPTIONS_MAP[code].components ?? [];
}

function toRow(
  option: ComponentOption,
  fallbackCode: string,
  value: number | null
): ReducedObservationComponent {
  const units = option.units.map((unit) => ({ k: unit, v: unit }));
  return {
    code: option.code ?? fallbackCode,
    display: option.display ?? fallbackCode,
    value,
    unit: units[0]?.k ?? null,
    units,
  };
}

/** A finite number, or null. Never NaN — see the module's spec for why. */
function toNumber(raw: string): number | null {
  if (!raw) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Which component set this reading belongs to, and one value per row of it.
 * Returning nulls (rather than nothing) keeps the template rows on screen so
 * the curator can type the value in while reading the highlighted page.
 */
function parseComponentValues(
  code: string,
  raw: string,
  sets: ComponentOption[][]
): { setIndex: number; values: (number | null)[] } {
  if (code === BLOOD_PRESSURE) {
    const match = raw.match(BLOOD_PRESSURE_PATTERN);
    return {
      setIndex: 0,
      values: match ? [Number(match[1]), Number(match[2])] : [null, null],
    };
  }

  if (code === BODY_HEIGHT) {
    const imperial = raw.match(FEET_INCHES_PATTERN);
    if (imperial) {
      return {
        setIndex: 1,
        values: [Number(imperial[1]), imperial[2] ? Number(imperial[2]) : 0],
      };
    }
    const centimetres = raw.match(CENTIMETRES_PATTERN);
    if (centimetres) {
      return { setIndex: 0, values: [Number(centimetres[1]) / 100] };
    }
    const metres = raw.match(METRES_PATTERN);
    return { setIndex: 0, values: [metres ? Number(metres[1]) : null] };
  }

  // No parser for this code: fill a single-row set, blank a multi-row one.
  const rows = sets.at(0);
  return {
    setIndex: 0,
    values: rows?.length === 1 ? [toNumber(raw)] : (rows ?? []).map(() => null),
  };
}

/**
 * Turn discern's flat `vital` OCR labels into the seed shape the Vital Sign /
 * Body form expects.
 *
 * The contract that matters: `getMeasurementArray` matches these rows against
 * the template **by component code**, so a blood pressure must arrive as
 * `systolic` + `diastolic` rows and not as one `blood-pressure` row. discern
 * sends the page text verbatim ("116/73", `6'0"`) by design — it is what the
 * bounding box points at — so splitting it is Engage's job.
 *
 * Idempotent: `mergeOcrFromConfig` runs it twice, both times off `_bboxMap`.
 */
export function assembleVitalSignOcrRecord(
  flat: Record<string, unknown>
): VitalSignOcrRecord {
  const code = asString(flat.code);
  const testDate = asString(flat.testDate);
  const raw = asString(flat.value);
  const ocrUnit = asString(flat.unit) || null;

  if (!code || !(code in ALL_OBSERVATION_OPTIONS_MAP)) {
    return { code, testDate, unit: ocrUnit, components: [] };
  }

  const sets = componentSets(code);
  const { setIndex, values } = parseComponentValues(code, raw, sets);
  const options = sets.at(setIndex);

  // Codes with no component sets (heart-rate, temperature, pain, ...) carry
  // their measurement on the option itself.
  if (!options?.length) {
    const option = ALL_OBSERVATION_OPTIONS_MAP[code];
    return {
      code,
      testDate,
      unit: ocrUnit,
      components: [
        toRow(
          {
            code,
            display: option.display,
            units: ocrUnit ? [ocrUnit] : (option.units ?? []),
          },
          code,
          toNumber(raw)
        ),
      ],
    };
  }

  const components = options.map((option, index) =>
    toRow(option, code, values[index] ?? null)
  );

  return {
    code,
    testDate,
    // Mirrors how FormDrilldown derives its set list: the first unit of the
    // first component of each set.
    unit: components[0].unit ?? ocrUnit,
    components,
  };
}
